import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsTab({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, auth);
      setReviews(data.reviews || []);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusy(r.id);
    try {
      await axios.post(`${API}/admin/reviews/${r.id}/approve`, {}, auth);
      toast.success("Review approved — it's now live on the site.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve review.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    setBusy(r.id);
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`, auth);
      toast.success("Review deleted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete review.");
    } finally {
      setBusy(null);
    }
  };

  const pending = reviews.filter((r) => r.status === "pending");
  const approved = reviews.filter((r) => r.status === "approved");

  const Card = ({ r, isPending }) => (
    <div className="border border-[rgba(201,162,74,0.25)] p-5" data-testid={`review-card-${r.id}`}>
      <p className="font-serif-alt italic text-lg text-[var(--cream)] leading-relaxed">“{r.text}”</p>
      <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[var(--muted)] mt-3">
        {r.name}{r.date_label ? ` · ${r.date_label}` : ""}{r.booking_ref ? ` · ${r.booking_ref}` : ""}
      </p>
      <div className="flex gap-3 mt-4">
        {isPending && (
          <button data-testid={`review-approve-${r.id}`} onClick={() => approve(r)} disabled={busy === r.id}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)] text-[var(--bg)] px-4 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-[var(--cream)] transition-colors duration-200 disabled:opacity-50">
            {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
          </button>
        )}
        <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)} disabled={busy === r.id}
          className="inline-flex items-center gap-2 border border-[rgba(201,162,74,0.4)] text-[var(--muted)] px-4 py-2 text-[11px] uppercase tracking-[0.15em] hover:text-red-300 hover:border-red-300 transition-colors duration-200 disabled:opacity-50">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)] mx-auto" />;

  return (
    <div className="max-w-3xl mx-auto space-y-10" data-testid="reviews-tab">
      <div>
        <h2 className="font-display text-3xl text-[var(--cream)] mb-1">Awaiting approval</h2>
        <p className="font-body text-sm text-[var(--muted)] mb-5">New reviews only appear on the site once you approve them.</p>
        <div className="space-y-4" data-testid="pending-reviews-list">
          {pending.length === 0 && <p className="font-serif-alt text-lg text-[var(--muted)]" data-testid="no-pending-reviews">No reviews waiting for approval.</p>}
          {pending.map((r) => <Card key={r.id} r={r} isPending />)}
        </div>
      </div>
      <div>
        <h2 className="font-display text-3xl text-[var(--cream)] mb-5">Live on the site</h2>
        <div className="space-y-4" data-testid="approved-reviews-list">
          {approved.length === 0 && <p className="font-serif-alt text-lg text-[var(--muted)]">No approved reviews yet.</p>}
          {approved.map((r) => <Card key={r.id} r={r} isPending={false} />)}
        </div>
      </div>
    </div>
  );
}
